import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'

import { Button, Page, Radiobutton, ServiceCheckbox, Spinner, StopMarker, StopsRouteDots } from '../../../components'
import { ListStopsView, MapStopsView, SearchStopsView, StopSearchHeader } from '../../../containers'
import { images } from '../../../images'
import { IBase, ILocation, ISearchedType, IServiceInfo, IStop } from '../../../models'
import { urls } from '../../../routing'
import { LiteralsService } from '../../../services'
import {
  addCachedAddressAction,
  checkSessionAction,
  fetchDestinationBusStopsAction,
  fetchClientInfoAction,
  fetchServicesAction,
  loadCachedAddressesAction,
  resetAreaChangedAction,
  resetNewReservationFieldsAction,
  resetSingleBusStopAction,
  setBusStopAction,
  setIsSeachingAction,
  setMapChangedCenterAction,
  setRoundTripAction,
  setServiceSelectedAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { ReservationInstructions } from '../../../containers/NewReservation/Instructions'
import literalsService from '../../../services/literals-service'
import theme from '../../../theme'
import { appsStyleSelected } from '../../../helpers/app'
import { compareStops, getServiceAreaCenter } from '../../../helpers'
import { Application } from '../../../config'
import { AnyAction } from 'redux'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  serviceInfos: IServiceInfo[]
  isFetchingServices: boolean
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
  pickedService: number | null
  isSearching: boolean
  coordinates: ILocation | null
  isPenalized: boolean
  town: IBase
  searchedType: ISearchedType | null
  isMarkerSelected: boolean
  areaChanged: boolean
  isRoundTrip: boolean
  pickedTownId: number
  isEdit: boolean
  showTripTypeOptions: boolean
  singleStopSelected?: IStop
  isSearchingCoordsInMap: boolean
  isMapCenterWithinOutlines: boolean
  previewOriginStop?: IStop
  previewDestinationStop?: IStop
  isGuest: boolean
  onRequestNavigateToDate?: () => void
}

interface IState {
  isInListMode: boolean
  filterOpened: boolean
  filterUpdated: boolean
  toggledMode: boolean
  forwardDirection: boolean
}

const { isIframe } = Application

class BusStopsStep extends PureComponent<IProps, IState> {

  constructor(props: IProps) {
    super(props)

    this.state = {
      isInListMode: false,
      filterOpened: false,
      filterUpdated: false,
      toggledMode: false,
      forwardDirection: true
    }
  }
  
  private get viewMode(): ReactNode {
    const { isInListMode } = this.state
    const ListStopsViewAny = ListStopsView as any

    if (isInListMode) return <ListStopsViewAny onRequestNavigateToDate={ this.props.onRequestNavigateToDate } />
    else return <MapStopsView />
  }

  private get isEveryFieldValid(): boolean {
    const { pickedOriginBusStop, pickedDestinationBusStop, singleStopSelected } = this.props
    if (singleStopSelected) {
      if (pickedOriginBusStop) return compareStops(singleStopSelected, pickedOriginBusStop)
      if (pickedDestinationBusStop) return compareStops(singleStopSelected, pickedDestinationBusStop)
      return false
    }
    return (!!pickedOriginBusStop && !!pickedDestinationBusStop)
  }

  private get instructions(): ReactNode {
    const { pickedOriginBusStop, pickedDestinationBusStop, serviceInfos, isMarkerSelected, singleStopSelected, showTripTypeOptions } = this.props

    let title = ''
    let text = ''
    let backgroundColor = theme.colors.greyLight

    const allServicesSingle = serviceInfos.every((serviceInfo: IServiceInfo) => serviceInfo.singleStopReservations)
    const allServicesNotSingle = serviceInfos.every((serviceInfo: IServiceInfo) => !serviceInfo.singleStopReservations)
    if (allServicesSingle) {
      const allHaveRoundTrips = serviceInfos.every((serviceInfo: IServiceInfo) => serviceInfo.roundTrips)
      title = literalsService.get('tutorialSingleStopTitle', true)
      text = isMarkerSelected
        ? literalsService.get('tutorialConfirmSingleStopContent', true)
        : literalsService.get(allHaveRoundTrips ? 'tutorialSingleStopRoundTripContent' : 'tutorialSingleStopContent', true)
      if (
        (singleStopSelected && pickedOriginBusStop && compareStops(singleStopSelected, pickedOriginBusStop)) ||
        (singleStopSelected && pickedDestinationBusStop && compareStops(singleStopSelected, pickedDestinationBusStop))
      ) {
        title = literalsService.get('tutorialRoundTripTitle', true)
        text = literalsService.get('tutorialRoundTripContent', true)
      }
    } else if (allServicesNotSingle) {
      if (
        (singleStopSelected && pickedOriginBusStop && compareStops(singleStopSelected, pickedOriginBusStop)) ||
        (singleStopSelected && pickedDestinationBusStop && compareStops(singleStopSelected, pickedDestinationBusStop))
      ) {
        title = literalsService.get('tutorialRoundTripTitle', true)
        text = literalsService.get('tutorialRoundTripContent', true)
      } else if (pickedOriginBusStop && pickedDestinationBusStop && showTripTypeOptions) {
        title = literalsService.get('tutorialRoundTripTitle', true)
        text = literalsService.get('tutorialRoundTripContent', true)
      } else {
        // Door to door adds the "or move the pin" half to the instructions.
        const d2d = this.props.isSearchingCoordsInMap ? 'D2D' : ''
        title = !!pickedOriginBusStop ?  literalsService.get('tutorialChooseDestinationTitle', true) : literalsService.get('tutorialChooseOriginTitle', true)
        text = !!pickedOriginBusStop ?
          (isMarkerSelected) ?
            literalsService.get('tutorialConfirmDestinationContent', true) :
            literalsService.get(`tutorialChooseDestinationContent${d2d}`, true) :
          (isMarkerSelected) ?
            literalsService.get('tutorialConfirmOriginContent', true) :
            literalsService.get(`tutorialChooseOriginContent${d2d}`, true)
        backgroundColor = !!pickedOriginBusStop ? theme.colors.lightGreen : theme.colors.accentLight
      }
    } else {
      title = literalsService.get('tutorialGenericTitle', true)
      text = literalsService.get('tutorialGenericContent', true)
    }

    return (
      <ReservationInstructions
        textTitle={ title }
        textContent={ text }
        backgroundColor={ backgroundColor }
      />
    )
  }

  // Door to door: the footer confirms the pin's position instead of moving on.
  private get door2DoorAction(): ReactNode {
    const {
      isSearchingCoordsInMap,
      isMapCenterWithinOutlines,
      previewOriginStop,
      previewDestinationStop,
      pickedOriginBusStop,
      pickedDestinationBusStop
    } = this.props

    if (!isSearchingCoordsInMap) return null
    if (pickedOriginBusStop && pickedDestinationBusStop) return null

    if (!isMapCenterWithinOutlines) {
      return (
        <Button
          title={ LiteralsService.get('tryAnotherAddress') }
          onPress={ this.recenterOnServiceArea }
          styles={{ boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40` }}
          filled
          upper
        />
      )
    }

    const isOrigin = !pickedOriginBusStop
    const previewStop = isOrigin ? previewOriginStop : previewDestinationStop
    if (!previewStop) return null

    return (
      <Button
        title={ LiteralsService.get(isOrigin ? 'confirmPickupDoor2Door' : 'confirmDestinationDoor2Door') }
        onPress={ () => this.confirmDoor2DoorStop(previewStop) }
        styles={{ boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40` }}
        filled
        upper
      />
    )
  }

  private recenterOnServiceArea = () => {
    const { dispatch, serviceInfos } = this.props
    const center = getServiceAreaCenter(serviceInfos)

    if (center) dispatch(setMapChangedCenterAction(center))
  }

  private confirmDoor2DoorStop = (previewStop: IStop) => {
    const { dispatch } = this.props
    const { lat, lng } = previewStop.location as any
    const location = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: [lng, lat] }
    }

    dispatch(setBusStopAction({ stop: { ...previewStop, location } as unknown as IStop }))

    if (previewStop.id && previewStop.name) {
      dispatch(addCachedAddressAction({
        id: String(previewStop.id),
        name: previewStop.name,
        location: location as unknown as ILocation,
        usedAt: Date.now()
      }))
    }
  }

  private get displayNextButton(): ReactNode {
    return (
      <Button
        title={ LiteralsService.get('next') }
        onPress={ this.handleNextPress }
        isDisabled={ !this.isEveryFieldValid }
        styles={{
          boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
        }}
        filled
        upper
      />
    )
  }

  private handleNextPress = () => {
    if (this.props.onRequestNavigateToDate) {
      this.props.onRequestNavigateToDate()
      return
    }
    const { history } = this.props
    const name = new URLSearchParams(location.search).get('name') ?? history.location.state?.name
    const lastName = new URLSearchParams(location.search).get('lastName') ?? history.location.state?.lastName
    const email = new URLSearchParams(location.search).get('email') ?? history.location.state?.email
    const townId = new URLSearchParams(location.search).get('townId') ?? history.location.state?.townId

    history.push(urls.newReservationDateStep, {
      userData: { name, lastName, email },
      townId
    })
  }

  private stopIcons(single: boolean, isOrigin?: boolean): ReactNode {
    if (!single) return (
      <StopsRouteDots
        colorStart={ theme.colors.accent }
        colorEnd={ theme.colors.greenRadio }
        horizontal={ false }
      />
    )
    return <StopMarker color={ isOrigin ? theme.colors.accent : theme.colors.greenRadio } />
  }

  private get stopsSummary(): ReactNode {
    const { pickedOriginBusStop, pickedDestinationBusStop, previewOriginStop, previewDestinationStop } = this.props

    // Door to door: show the address under the pin as soon as the map settles,
    // so the user reads where they are about to be picked up before confirming.
    const originPreview = pickedOriginBusStop ? undefined : previewOriginStop
    const destinationPreview = pickedOriginBusStop && !pickedDestinationBusStop ? previewDestinationStop : undefined

    const origin = pickedOriginBusStop?.name ?? originPreview?.name ?? LiteralsService.get('origin', true)
    const destination = pickedDestinationBusStop?.name ?? destinationPreview?.name ?? LiteralsService.get('destination', true)
    const isSingle = pickedOriginBusStop?.serviceInfo && pickedOriginBusStop.serviceInfo.singleStopReservations || pickedDestinationBusStop?.serviceInfo && pickedDestinationBusStop.serviceInfo.singleStopReservations
    return (
      <section className={`stops-summary-container ${isSingle ? 'single' : ''}`}>
        <div>
          { this.stopIcons(isSingle!, pickedOriginBusStop !== undefined) }
        </div>
        <div  className="stops-summary">
          {
            (isSingle && pickedOriginBusStop || !isSingle) &&
            <div className='stops-summary-row'>
              <div>
                <span className={ pickedOriginBusStop ? '' : originPreview ? 'preview' : 'placeholder' }>
                  { origin }
                </span>
              </div>
              {
                pickedOriginBusStop && 
                <div className='reset-button' onClick={this.handleResetOriginStop}>
                  <img  src={images['cross']} alt='reset-origin' />
                </div>
              }
            </div>
          }
          {
            (isSingle && pickedDestinationBusStop || !isSingle) &&
            <div className='stops-summary-row'>
              <div>
                <span className={ pickedDestinationBusStop ? '' : destinationPreview ? 'preview' : 'placeholder' }>
                  { destination }
                </span>
              </div>
              {
                pickedDestinationBusStop && 
                <div className='reset-button' onClick={this.handleResetDestinationStop}>
                  <img  src={images['cross']} alt='reset-destination' />
                </div>
              }
            </div>
          }
        </div>
      </section>
    )
  }

  public componentDidMount() {
    const { dispatch, town, history, pickedOriginBusStop, pickedDestinationBusStop, isGuest, isEdit } = this.props
    const urlTownId = new URLSearchParams(location.search).get('townId')
    const stateTownId = (history.location.state as any)?.townId
    const townIdToStore = urlTownId ?? stateTownId
    if (townIdToStore) sessionStorage.setItem('iframeTownId', String(townIdToStore))

    // An edit keeps the booked stop picked, so it can go on with it or pick another one.
    const stop: any = pickedDestinationBusStop || pickedOriginBusStop
    if (!isEdit && stop?.serviceInfo?.singleStopReservations) {
      dispatch(resetSingleBusStopAction())
    }

    dispatch(loadCachedAddressesAction())

    // Guest mode has no user town: the town chosen in the towns modal is the
    // one carried by the URL.
    if (isGuest && urlTownId) this.loadGuestTown()
    else if (!town.id) dispatch(checkSessionAction())
    else dispatch(fetchServicesAction())
  }

  public componentDidUpdate(prevProps: IProps) {
    const { pickedOriginBusStop, dispatch, town, areaChanged, serviceInfos, pickedTownId, isEdit, history, isGuest, location: currentLocation } = this.props

    // Either the towns modal just put the town in the URL, or the session check
    // recognised a direct entry with ?townId as a guest.
    if (isGuest) {
      const urlTownId = new URLSearchParams(currentLocation.search).get('townId')
      const prevUrlTownId = new URLSearchParams(prevProps.location.search).get('townId')
      if (urlTownId && (urlTownId !== prevUrlTownId || !prevProps.isGuest)) this.loadGuestTown()
    }

    const allStops = serviceInfos.flatMap((serviceInfo: IServiceInfo) => {
      return serviceInfo.stops.filter((s: IStop) => this.state.forwardDirection ? (s.direction == 0 || s.direction == null) : (s.direction == 1 || s.direction == null))
    });
    if (!this.state.toggledMode) {
      const explicitListPreference = serviceInfos.find((si: IServiceInfo) => si.showStopsListFirst != null)?.showStopsListFirst
      const isInListMode = explicitListPreference === true
      this.setState({ isInListMode })
    }

    if (areaChanged) {
      dispatch(resetAreaChangedAction())
      // A signed in user picked another town in the towns modal: from now on the profile town is
      // the one being booked, so a ?townId left over from the guest flow must stop winning over it,
      // and the stops on screen are reloaded for the new town.
      if (!isGuest && !isEdit) {
        const hadUrlTown = new URLSearchParams(currentLocation.search).get('townId') || history.location.state?.townId
        if (hadUrlTown) {
          history.replace(urls.newReservationBusStopsStep)
          // The modal already asked for the client info, but with the URL town still in place.
          dispatch(fetchClientInfoAction())
        }
        dispatch(resetNewReservationFieldsAction())
        dispatch(fetchServicesAction())
      }
    }

    // A door to door origin is a plain map point, so it carries no serviceInfo.
    const originAllowsDestinations = this.props.isSearchingCoordsInMap ||
      (!!pickedOriginBusStop?.serviceInfo && !pickedOriginBusStop.serviceInfo.singleStopReservations)

    if (
      pickedOriginBusStop &&
      pickedOriginBusStop !== prevProps.pickedOriginBusStop &&
      originAllowsDestinations
    ) {
      let townId = new URLSearchParams(location.search).get('townId')
      if (!townId) townId = history.location.state?.townId
      dispatch(
        fetchDestinationBusStopsAction({
          stop: pickedOriginBusStop,
          idTown: isEdit ? pickedTownId : townId ? Number(townId) : town.id
        })
      )
    }

    // Resetting the fields would also drop the edit, and the trip would be booked anew.
    if (this.state.filterUpdated && isEdit) {
      this.setState({ filterUpdated: false })
    } else if (this.state.filterUpdated) {
      if (pickedOriginBusStop) {
        const foundInServices = serviceInfos.filter((serviceInfo) => serviceInfo.stops.some((stop) => stop.id === pickedOriginBusStop.id))
        if (foundInServices && foundInServices.length === 1) dispatch(resetNewReservationFieldsAction())
      } else {
        dispatch(resetNewReservationFieldsAction())
      }
      this.setState({ filterUpdated: false })
    }
    
    // A guest never gets a user town, so it would refetch on every update. A guest who just
    // signed in already has the URL town's services on screen: reloading them would only reset
    // the filter, and the profile town is not the one being booked.
    const urlTownId = new URLSearchParams(currentLocation.search).get('townId')
    if (!isGuest && prevProps.town.id === 0 && town.id && !(urlTownId && serviceInfos.length)) {
      dispatch(fetchServicesAction())
    }
  }

  public render(): ReactNode {

    const { isFetchingServices, serviceInfos, isSearching, showTripTypeOptions, isRoundTrip, isEdit } = this.props
    const { isInListMode, filterOpened } = this.state

    if (isFetchingServices && !serviceInfos.length) {
      return (
        <Page className={`authenticated ${appsStyleSelected?.style}`}>
            <article className="spinner-container">
              <Spinner />
            </article>
          </Page>
      )
    }

    const servicesHasRoundTrip = serviceInfos.some((serviceInfo) => serviceInfo.roundTrips)

    return (
      <Page className={`authenticated ${appsStyleSelected?.style}`}>
        <StyledArticle className={ `fit-viewport ${ this.state.isInListMode ? 'list-mode' : '' } ${appsStyleSelected?.style}` }>
          <section className={`bus-stops ${this.isSingleAndRoundTrip && !isEdit ? 'no-footer' : ''}`}>
            { !isIframe &&
              <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className='row'>
                  <h3>
                    { LiteralsService.get('desiredRoute').toUpperCase() }
                  </h3>
                </div>
                <div className='row'>
                  { this.isEveryFieldValid && !servicesHasRoundTrip && !this.props.showTripTypeOptions ? null : this.instructions }
                </div>
                <div className='row modeButton'>
                  <label className={isInListMode ? 'selected' : ''} onClick={() => this.setState({ isInListMode: true, toggledMode: true })}>{ LiteralsService.get('stopsList').toUpperCase() }</label>
                  <div className='separator'></div>
                  <label className={!isInListMode ? 'selected' : ''} onClick={() => this.setState({ isInListMode: false, toggledMode: true })}>{ LiteralsService.get('stopsMap').toUpperCase() }</label>
                </div>
              </header>
            }
            { isIframe &&
              <>
                <div style={{ paddingTop: 8 }}>
                  { (this.isEveryFieldValid && !servicesHasRoundTrip && !this.props.showTripTypeOptions) ? null : this.instructions }
                </div>
                <header>
                  <label className={isInListMode ? 'selected' : ''} onClick={() => this.setState({ isInListMode: true, toggledMode: true })}>{ LiteralsService.get('stopsList').toUpperCase() }</label>
                  <div className='separator'></div>
                  <label className={!isInListMode ? 'selected' : ''} onClick={() => this.setState({ isInListMode: false, toggledMode: true })}>{ LiteralsService.get('stopsMap').toUpperCase() }</label>
                </header>
              </>
            }
            {
              <div className='search-bar-container'>
                <StopSearchHeader
                  filterOpened={this.state.filterOpened}
                  onSearchPress={this.handleSearchMode}
                  onFilterPress={this.onFilterPress}
                  moreThanOneServiceInfo={ serviceInfos.length > 1 }
                />
                {
                  isSearching &&
                  <div className='search-results-container'>
                    <SearchStopsView />
                  </div>
                }
                {
                  filterOpened &&
                  <div className='filter-container'>
                    <div className='filter-header'>
                      <span className='title'>
                        { LiteralsService.get('services', true) }
                      </span>
                      <img className='close-icon' src={ images['cross'] } onClick={ () => this.onFilterPress(false) } alt='close-filter' />
                    </div>
                    {
                      serviceInfos
                      .sort((a,b) => a.tag.concat(a.name) > b.tag.concat(b.name) ? 1:-1)
                      .map((s) => {
                        return <ServiceCheckbox key={s.id} serviceInfo={s} onServiceSelected={ this.onSelectServiceInfo } />
                      })
                    }
                  </div>
                }
              </div>
            }
            { this.viewMode }
          </section>
          { (!this.isSingleAndRoundTrip || isEdit) &&
            <footer>
              { !this.isSingleAndRoundTrip && this.stopsSummary }
              <section className='buttons-wrapper'>
                { this.door2DoorAction ?? this.displayNextButton }
                {
                  !isEdit && showTripTypeOptions &&
                  <div className='radiobuttons-container'>
                    <div onClick={ () => this.onSelectRoundTrip(true) }>
                      <Radiobutton
                        selected={ isRoundTrip }
                        label={ literalsService.get('newReservationType.roundTrip', true) }
                      />
                    </div>
                    <div onClick={ () => this.onSelectRoundTrip(false) }>
                      <Radiobutton
                        selected={ !isRoundTrip }
                        label={ literalsService.get('newReservationType.oneWay', true) }
                        description={ `(${literalsService.get('newReservationType.oneWayOptions')})` }
                      />
                    </div>
                  </div>
                }
              </section>
            </footer>
          }
        </StyledArticle>
      </Page>
    )
  }

  // Only the services: client-info is not readable without a session and the
  // stops map does not need it.
  private loadGuestTown = (): void => {
    this.props.dispatch(fetchServicesAction())
  }

  // TODO: Refactor this to redux
  private handleToggleMode = (): void => {
    const { isInListMode } = this.state

    this.setState({ isInListMode: !isInListMode })
  }

  private handleSearchMode = (searchMode: boolean): void => {
    this.props.dispatch(setIsSeachingAction(searchMode))
  }

  private onFilterPress = (showFilter?: boolean) => {
    const { filterOpened } = this.state
    this.setState({
      filterOpened: showFilter ?? !filterOpened
    })
  }

  private onSelectServiceInfo = (serviceInfo: IServiceInfo): void => {
    const { dispatch } = this.props
    dispatch(setServiceSelectedAction(serviceInfo, !serviceInfo.selected))
    this.setState({ filterUpdated: true })
  }

  private onSelectRoundTrip = (roundTrip: boolean) => {
    const { dispatch } = this.props
    dispatch(setRoundTripAction(roundTrip))
  }

  private handleResetOriginStop = () => {
    const { pickedOriginBusStop, dispatch } = this.props

    dispatch(setBusStopAction({ stop: pickedOriginBusStop! }))
  }

  private handleResetDestinationStop = () => {
    const { pickedDestinationBusStop, dispatch } = this.props

    dispatch(setBusStopAction({ stop: pickedDestinationBusStop! }))
  }

  private get isSingleAndRoundTrip(): boolean {
    const { serviceInfos } = this.props
    return serviceInfos.every((serviceInfo) => serviceInfo.singleStopReservations && serviceInfo.roundTrips)
  }
}

const mapStateToProps = ({ newReservation, userInfo, configuration }: IStoreState) => ({
  serviceInfos: newReservation.serviceInfos,
  isFetchingServices: newReservation.isFetchingServices,
  pickedOriginBusStop: newReservation.pickedOriginBusStop,
  pickedDestinationBusStop: newReservation.pickedDestinationBusStop,
  pickedService: newReservation.pickedService,
  isSearching: newReservation.isSearching,
  coordinates: newReservation.coordinates,
  isPenalized: userInfo.isPenalized,
  town: userInfo.town,
  searchedType: newReservation.searchedType,
  isMarkerSelected: newReservation.isMarkerSelected,
  areaChanged: configuration.areaChanged,
  isRoundTrip: newReservation.isRoundTrip,
  pickedTownId: newReservation.pickedTown,
  isEdit: newReservation.isEdit,
  showTripTypeOptions: newReservation.showTripTypeOptions,
  singleStopSelected: newReservation.singleStopSelected,
  isSearchingCoordsInMap: newReservation.isSearchingCoordsInMap,
  isMapCenterWithinOutlines: newReservation.isMapCenterWithinOutlines,
  previewOriginStop: newReservation.previewOriginStop,
  previewDestinationStop: newReservation.previewDestinationStop,
  isGuest: userInfo.isGuest
})

export default withRouter(connectRedux(mapStateToProps, BusStopsStep as any))
